import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { BookOpen, Calendar, Download, Eye, Pencil, X } from 'lucide-react'

import {
  Avatar,
  Banner,
  Button,
  ConfirmDialog,
  DataTable,
  EmptyState,
  Field,
  KeyValue,
  LoadingSkeleton,
  Modal,
  Money,
  PageHeader,
  Pagination,
  QueryBoundary,
  Section,
  StatTile,
  StatusBadge,
  TextInput,
  useToast,
  type Column,
} from '../../../components'
import { paisaToRupees } from '@cattle/money'

import {
  useBook,
  useBusiness,
  useTickets,
  type BookAnimal,
  type BookSummary,
} from '../../../fixtures'
import { useIsPlatformOwner } from '../../../app/session'
import { BookAnimalDrawer, GrantImage } from '../BookAnimalDrawer'
import { ApiRequestError } from '../../../lib/apiClient'
import { downloadCsv } from '../../../lib/csv'
import { formatDateShort, todayIso } from '../../../lib/format'
import { t } from '../../../lib/i18n'
import { useAccessGrantFor, usePlatformAccess } from '../../../lib/platformAccess'
import { routes } from '../../../lib/routes'

/**
 * P-04 Inside a business.
 *
 * This is what full access buys: a support call answered on one screen instead
 * of a phone call and guesswork. Three rules hold it together.
 *
 * 1. A book cannot be opened without a REASON. The reason modal is not
 *    dismissable into the content — cancelling leaves for the business page.
 *    The reason is written to the access log and cannot be edited afterwards.
 *    It is not a client-side courtesy: `POST /platform/businesses/:id/access`
 *    writes the immutable `access_log` row FIRST and refuses a reason shorter
 *    than three characters with a 400, so there is no way past this modal that
 *    does not leave a row saying who opened the book and why.
 * 2. Read-only is the default and editing is a SECOND, explicit confirm, so a
 *    trader's book cannot be changed by accident while it is being looked at.
 *    Only the platform owner may switch; support has no such control at all.
 * 3. Cost, break-even and profit are READ, never computed. The engine that
 *    produces them is the trader's, and a console that recalculated them would
 *    eventually disagree with the app he is holding.
 *
 * The export is the fourth thing worth stating: there is NO book-export
 * endpoint (CONTRACT GAP — `GET /reports/export` is the trader's own route, not
 * a platform one), so Export writes the animals table in the browser from the
 * rows on screen. Money that is absent from the payload — the server omits a
 * `...Paisa` key for an actor without `platform.billing`, and under the
 * tenant's Price Lock — is written as an EMPTY CELL. A zero would be this
 * console making a claim about a trader's money that nobody made to it, and it
 * would be a claim in a file, which outlives the session.
 *
 * The grant returns a SCOPED session token, one hour long, and the console's own
 * cookie session is deliberately not bound to it — every in-book request carries
 * that token instead. Leaving closes the grant, which is what stamps the visit's
 * duration onto the log.
 */

/** A clock, not a date. `todayIso()` owns the day; this owns the minute. */
function clockTime(at: Date): string {
  const hours = String(at.getHours()).padStart(2, '0')
  const minutes = String(at.getMinutes()).padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * A rupee figure for a CSV cell, and whether it was there at all.
 *
 * `undefined` is a first-class state here exactly as it is in `<Money>`: the
 * figure is ABSENT from the payload, not zero.
 */
const rupees = (paisa: number | undefined): string =>
  paisa === undefined ? '' : String(paisaToRupees(paisa))

export function InsideBusinessScreen() {
  const params = useParams<{ slug: string }>()
  const slug = params.slug ?? ''
  const navigate = useNavigate()
  const isOwner = useIsPlatformOwner()
  const toast = useToast()

  const book = useBook(slug)
  const business = useBusiness(slug)
  const tickets = useTickets()

  const { open, setMode, close } = usePlatformAccess()
  const access = useAccessGrantFor(slug)

  const [reason, setReason] = useState('')
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  const [confirmEdit, setConfirmEdit] = useState(false)
  /** The animal whose full record is open in the drawer. */
  const [openAnimalId, setOpenAnimalId] = useState<string | null>(null)
  const [elapsed, setElapsed] = useState(0)

  const editing = access?.mode === 'EDIT'

  /* The banner states how long this visit has been open, because a session
     left open on someone else's book is the thing the access log is for. */
  useEffect(() => {
    if (!access) return
    const openedAt = access.openedAtMs
    const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - openedAt) / 60_000)))
    tick()
    const timer = window.setInterval(tick, 30_000)
    return () => window.clearInterval(timer)
  }, [access])

  /* The banner names the book before the book itself has arrived, so the slug
     is the last resort rather than an empty possessive. */
  const name = business.data?.name ?? book.data?.name ?? slug

  /* Leaving CLOSES the grant. The duration on the log is stamped by P7, so a
     visit abandoned by navigating away is a visit with no end time on it. */
  const leave = () => {
    void close()
    navigate(routes.platform.business(slug))
  }

  const openBook = async () => {
    const businessId = business.data?.id
    if (!businessId) return
    setOpening(true)
    setOpenError(null)
    try {
      await open(businessId, { reason: reason.trim(), mode: 'READ_ONLY' })
    } catch (cause) {
      // The server owns the rule, so its wording is shown rather than a second
      // copy of the same validation. `VALIDATION_FAILED` carries the field-level
      // message — "Say why you are opening this book." — and the envelope's
      // generic line is only the fallback.
      const fieldMessage =
        cause instanceof ApiRequestError ? cause.fieldErrors?.reason?.[0] : undefined
      setOpenError(
        fieldMessage ??
          (cause instanceof ApiRequestError ? cause.message : t('state.errorTitle')),
      )
    } finally {
      setOpening(false)
    }
  }

  /*
    THE EXPORT — the animals table of the book currently open, written here
    because no endpoint serves it. It is the rows on screen and nothing else,
    and the toast says so rather than implying a complete statement was
    prepared for anybody.
  */
  const exportBook = (data: BookSummary) => {
    if (data.animals.length === 0) {
      toast.show(t('p.inside.exportEmpty'), 'info')
      return
    }

    // Whether ANY money reached this browser at all. Said afterwards, because a
    // file short of its money columns must not be sent to an accountant by
    // someone who did not notice.
    const blank = data.animals.some(
      (animal) =>
        animal.paidPaisa === undefined ||
        animal.costNowPaisa === undefined ||
        (animal.status !== 'SOLD' &&
          (animal.breakEvenPaisa === undefined || animal.askingPaisa === undefined)) ||
        (animal.status === 'SOLD' && animal.soldPaisa === undefined),
    )

    downloadCsv(
      `${t('p.inside.exportFilename')}-${slug}-${todayIso()}`,
      [
        t('animal.nickname'),
        t('animal.tagNumber'),
        t('animal.breed'),
        t('animal.teeth'),
        t('p.inside.colDays'),
        t('p.inside.colPaid'),
        t('p.inside.colCostNow'),
        t('p.inside.colBreakEven'),
        t('p.inside.colAsking'),
        t('p.inside.colSoldFor'),
        t('label.status'),
      ],
      data.animals.map((animal) => [
        animal.nickname,
        animal.tag,
        animal.breed,
        animal.teeth,
        animal.daysHeld,
        rupees(animal.paidPaisa),
        rupees(animal.costNowPaisa),
        // A sold animal has no break-even and no asking price. There is nothing
        // left to break even on, so the cell is empty rather than repeating the
        // last figure it happened to carry.
        animal.status === 'SOLD' ? '' : rupees(animal.breakEvenPaisa),
        animal.status === 'SOLD' ? '' : rupees(animal.askingPaisa),
        animal.status === 'SOLD' ? rupees(animal.soldPaisa) : '',
        t(`animalStatus.${animal.status}`),
      ]),
    )

    toast.show(t('p.inside.exported', { count: data.animals.length, name: data.name }))
    if (blank) toast.show(t('p.inside.exportBlank'), 'info')
  }

  if (!access) {
    return (
      <Modal
        open
        onClose={leave}
        title={t('p.inside.reasonTitle')}
        footer={
          <>
            <Button variant="ghost" onClick={leave}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              icon={<Eye className="size-4" />}
              disabled={reason.trim().length === 0 || opening || !business.data}
              onClick={() => void openBook()}
            >
              {t('p.inside.openReadOnly')}
            </Button>
          </>
        }
      >
        <p className="mb-4 text-[13.5px] leading-relaxed text-sub">{t('p.inside.reasonBody')}</p>
        <Field
          label={t('p.inside.reasonLabel')}
          hint={t('p.inside.reasonRequired')}
          htmlFor="access-reason"
          required
        >
          <TextInput
            id="access-reason"
            value={reason}
            onChange={setReason}
            placeholder={t('p.inside.reasonPlaceholder')}
            className="max-w-none"
          />
        </Field>
        {openError ? (
          <p
            role="alert"
            className="mt-3 rounded-r border border-danger/30 bg-danger/10 p-3 text-[12.5px] leading-relaxed text-danger"
          >
            {openError}
          </p>
        ) : null}
      </Modal>
    )
  }

  /* A slug that resolves to nothing is not a loading state. `useBook` returns
     `null` for an unknown business, which would otherwise skeleton for ever
     behind the boundary. */
  if (!book.isPending && !book.isError && !book.data) {
    return (
      <EmptyState
        icon={<BookOpen className="size-6" />}
        title={t('p.business.notFound')}
        body={t('p.business.notFoundBody')}
        action={
          <Button variant="primary" onClick={() => navigate(routes.platform.businesses())}>
            {t('p.nav.businesses')}
          </Button>
        }
      />
    )
  }

  const openedAtLabel = `${formatDateShort(todayIso())} ${clockTime(new Date(access.openedAtMs))}`

  const columns: Column<BookAnimal>[] = [
    {
      id: 'animal',
      header: t('label.animals'),
      sortBy: (animal) => animal.nickname,
      cell: (animal) => {
        /* The tag in a box: what this row shows when there is no photograph to
           show, and what it falls back to when the one there is will not load.
           A red "could not load" tile is right in the drawer, where one animal
           fills the screen; in forty rows it would be a wall of alarm about a
           thumbnail. The status code is still on the element for whoever asks.

           `thumb` is asked for unconditionally because `openVariant` falls back
           to the original for any variant it cannot find, and the list payload
           carries only `coverMediaId` — no `variants` array to consult. */
        const tagChip = (
          <span
            aria-hidden="true"
            className="flex size-[38px] shrink-0 items-center justify-center rounded-[10px] bg-accent-glass font-display text-[11px] font-bold tnum text-accent-dark"
          >
            {animal.tag}
          </span>
        )

        return (
        <div className="flex items-center gap-2.5">
          {animal.coverMediaId ? (
            <GrantImage
              mediaId={animal.coverMediaId}
              variant="thumb"
              token={access.token}
              alt={animal.nickname}
              className="size-[38px] shrink-0 rounded-[10px]"
              fallback={tagChip}
            />
          ) : (
            tagChip
          )}
          <span className="min-w-0">
            <b className="block truncate font-display text-[13.5px] font-bold text-dark">
              {animal.nickname}
            </b>
            <span className="block text-[11.5px] text-muted">
              {t('p.inside.tagNumber', { tag: animal.tag })}
            </span>
          </span>
        </div>
        )
      },
    },
    {
      id: 'breed',
      header: t('p.inside.colBreedTeeth'),
      secondary: true,
      cell: (animal) => (
        <span className="text-[12.5px] text-sub">{`${animal.breed} · ${animal.teeth}`}</span>
      ),
    },
    {
      id: 'days',
      header: t('p.inside.colDays'),
      numeric: true,
      sortBy: (animal) => animal.daysHeld,
      cell: (animal) => <span className="font-display font-bold tnum">{animal.daysHeld}</span>,
    },
    {
      id: 'paid',
      header: t('p.inside.colPaid'),
      numeric: true,
      sortBy: (animal) => animal.paidPaisa ?? 0,
      cell: (animal) => <Money paisa={animal.paidPaisa} symbol={false} />,
    },
    {
      id: 'costNow',
      header: t('p.inside.colCostNow'),
      numeric: true,
      sortBy: (animal) => animal.costNowPaisa ?? 0,
      cell: (animal) => <Money paisa={animal.costNowPaisa} symbol={false} />,
    },
    {
      id: 'breakEven',
      header: t('p.inside.colBreakEven'),
      numeric: true,
      /* A sold animal has no break-even. There is nothing left to break even
         on, and a figure here would be a number nobody can act on. */
      cell: (animal) =>
        animal.status === 'SOLD' ? (
          <span className="text-muted">{t('common.none')}</span>
        ) : (
          <Money paisa={animal.breakEvenPaisa} symbol={false} className="text-accent-dark" />
        ),
    },
    {
      id: 'asking',
      header: t('p.inside.colAsking'),
      numeric: true,
      cell: (animal) =>
        animal.status === 'SOLD' ? (
          <span className="inline-flex items-baseline gap-1">
            <span className="font-display text-[11px] font-bold text-success">
              {t('animalStatus.SOLD')}
            </span>
            <Money paisa={animal.soldPaisa} symbol={false} tone="positive" />
          </span>
        ) : (
          <Money paisa={animal.askingPaisa} symbol={false} />
        ),
    },
    {
      id: 'status',
      header: t('label.status'),
      cell: (animal) => (
        <StatusBadge
          tone={animal.status === 'SOLD' ? 'neutral' : animal.status === 'RESERVED' ? 'warn' : 'ok'}
        >
          {t(`animalStatus.${animal.status}`)}
        </StatusBadge>
      ),
    },
  ]

  return (
    <>
      <Banner
        tone="accent"
        icon={editing ? <Pencil className="size-full" /> : <Eye className="size-full" />}
        title={t('p.inside.bannerTitle', { name })}
        body={
          <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
            <StatusBadge tone={editing ? 'warn' : 'accent'}>
              {editing ? t('p.inside.editing') : t('p.inside.readOnly')}
            </StatusBadge>
            <span>{t('p.inside.bannerOpened', { when: openedAtLabel, reason: access.reason })}</span>
            <span aria-hidden="true">·</span>
            <span className="tnum">{t('p.inside.elapsed', { minutes: elapsed })}</span>
          </span>
        }
        action={
          <div className="flex items-center gap-2">
            {/* Switching to edit is the platform owner's alone. For support the
                control does not exist — it is not a disabled button. */}
            {isOwner && !editing ? (
              <Button
                size="sm"
                icon={<Pencil className="size-3.5" />}
                onClick={() => setConfirmEdit(true)}
              >
                {t('p.inside.switchToEdit')}
              </Button>
            ) : null}
            <Button size="sm" icon={<X className="size-3.5" />} onClick={leave}>
              {t('p.inside.leave')}
            </Button>
          </div>
        }
      />

      <QueryBoundary
        query={book}
        skeleton={
          <>
            <LoadingSkeleton variant="tiles" className="mb-3.5" />
            <LoadingSkeleton variant="table" rows={5} />
          </>
        }
      >
        {(data) => {
          if (!data) return null

          const ticket =
            data.ticket ??
            tickets.data?.find((item) => item.slug === slug && item.status === 'OPEN') ??
            tickets.data?.find((item) => item.slug === slug)

          /* Two rates he already has, subtracted for the note. Nothing here is
             a cost, a break-even or a profit — those are read as given. */
          const gapPaisa =
            data.perAnimalPerDayPaisa !== undefined && data.feedEstimatePaisa !== undefined
              ? data.perAnimalPerDayPaisa - data.feedEstimatePaisa
              : undefined

          return (
            <>
              <PageHeader
                title={
                  <span className="flex items-center gap-3.5">
                    <Avatar name={data.name} size="lg" />
                    {data.name}
                  </span>
                }
                subtitle={t('p.inside.headerSub', {
                  inStock: data.inStock,
                  sold: data.sold,
                  city: data.city,
                })}
                actions={
                  <>
                    <Button icon={<Calendar className="size-4" />}>
                      {t('p.inside.thisSeason')}
                    </Button>
                    <Button
                      icon={<Download className="size-4" />}
                      onClick={() => exportBook(data)}
                    >
                      {t('p.inside.exportBook')}
                    </Button>
                  </>
                }
              />

              <div className="mb-3.5 grid gap-3.5 sm:grid-cols-2 xl:grid-cols-4">
                <StatTile
                  surface="heavy"
                  label={t('p.inside.moneyTiedUp')}
                  value={<Money paisa={data.moneyTiedUpPaisa} short />}
                  sub={t('p.inside.moneyTiedUpSub')}
                />
                <StatTile
                  surface="heavy"
                  label={t('p.inside.costingHim')}
                  value={
                    <span className="inline-flex items-baseline gap-0.5">
                      <Money paisa={data.dailyCostPaisa} tone="warn" />
                      <small className="font-display text-[13px] font-semibold text-muted">
                        {t('unit.perDay')}
                      </small>
                    </span>
                  }
                  sub={t('p.inside.costingHimSub')}
                />
                <StatTile
                  surface="heavy"
                  label={t('p.inside.profitBooked')}
                  value={<Money paisa={data.profitBookedPaisa} signed tone="positive" />}
                  sub={t('p.inside.profitBookedSub', {
                    count: data.sold,
                    days: data.profitBookedDaysHeld,
                  })}
                />
                <StatTile
                  surface="heavy"
                  label={t('p.inside.owedToHim')}
                  value={<Money paisa={data.owedPaisa} tone="warn" />}
                  sub={t('p.inside.owedToHimSub')}
                />
              </div>

              <Section
                className="mb-3.5"
                title={t('p.inside.animals')}
                action={<span className="text-xs text-muted">{t('p.inside.animalsSub')}</span>}
              >
                <DataTable
                  columns={columns}
                  rows={data.animals}
                  rowKey={(animal) => animal.id}
                  caption={t('p.inside.animals')}
                  onRowClick={(animal) => setOpenAnimalId(animal.id)}
                />
                <Pagination shown={data.animals.length} total={data.inStock + data.sold} />
              </Section>

              <div className="grid gap-3.5 lg:grid-cols-2">
                <Section title={t('p.inside.monthlyCosts')}>
                  {data.monthlyCosts.map((cost) => (
                    <KeyValue
                      key={cost.label}
                      label={cost.label}
                      value={<Money paisa={cost.amountPaisa} />}
                    />
                  ))}
                  <KeyValue
                    label={t('p.inside.perAnimalPerDay')}
                    value={<Money paisa={data.perAnimalPerDayPaisa} tone="warn" />}
                  />
                  {/* The rupee figures are `<Money>` elements composed around the
                      sentence — never formatted strings poured into `t()`. */}
                  <p className="mt-3.5 border-t border-muted/15 pt-3.5 text-[12.5px] leading-relaxed text-sub">
                    <span className="inline-flex flex-wrap items-baseline gap-1">
                      <span>{t('p.inside.feedNoteEstimate')}</span>
                      <Money paisa={data.feedEstimatePaisa} className="text-[12.5px]" />
                    </span>{' '}
                    <span className="inline-flex flex-wrap items-baseline gap-1">
                      <span className="font-semibold text-dark">{t('p.inside.feedNoteGap')}</span>
                      <Money paisa={gapPaisa} tone="warn" className="text-[12.5px]" />
                      <span className="font-semibold text-dark">
                        {t('p.inside.feedNoteGapUnit')}
                      </span>
                    </span>
                  </p>
                </Section>

                {ticket ? (
                  <Section title={t('p.inside.whySupport')}>
                    <KeyValue label={t('p.inside.ticket')} value={`#${ticket.number}`} />
                    <KeyValue label={t('p.inside.raisedBy')} value={ticket.person} />
                    <KeyValue label={t('p.inside.about')} value={ticket.subject} />
                    {ticket.quote ? (
                      <blockquote className="mt-3.5 border-s-2 border-accent-glass-border ps-3.5 text-[12.5px] leading-relaxed text-sub italic">
                        {`“${ticket.quote}”`}
                      </blockquote>
                    ) : null}
                    {ticket.breakdown ? (
                      <div className="mt-3.5 border-t border-muted/15 pt-3">
                        <p className="font-display text-[13px] font-bold text-dark">
                          {t('p.inside.answer')}
                        </p>
                        <div className="mt-1">
                          {ticket.breakdown.map((line) => (
                            <KeyValue
                              key={line.label}
                              label={line.label}
                              value={<Money paisa={line.amountPaisa} />}
                            />
                          ))}
                        </div>
                        <p className="mt-3 text-[12.5px] leading-relaxed text-sub">
                          {t('p.inside.answerTail')}
                        </p>
                      </div>
                    ) : null}
                  </Section>
                ) : null}
              </div>
            </>
          )
        }}
      </QueryBoundary>

      {/* The second confirm is the whole point: read-only is what you opened,
          and editing another man's book is a separate decision. */}
      <ConfirmDialog
        open={confirmEdit}
        onClose={() => setConfirmEdit(false)}
        onConfirm={() => {
          // P6 · PLATFORM_OWNER only server-side, recorded on the access log,
          // and every write afterwards appears in the TRADER'S own audit log as
          // a platform action. Escalation is never a local flag.
          void setMode({ mode: 'EDIT', reason: access.reason })
            .then(() => toast.show(t('p.inside.toastEditing', { name }), 'info'))
            .catch((cause: unknown) =>
              toast.show(
                cause instanceof ApiRequestError ? cause.message : t('state.errorTitle'),
                'danger',
              ),
            )
        }}
        title={t('p.inside.editTitle', { name })}
        body={t('p.inside.editBody')}
        confirmLabel={t('p.inside.switchToEdit')}
        tone="accent"
      />

      <BookAnimalDrawer
        slug={slug}
        animalId={openAnimalId}
        token={access.token}
        onClose={() => setOpenAnimalId(null)}
      />
    </>
  )
}
